// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The model registry.
 *
 * Every entry here was fetched anonymously from huggingface.co on 2026-08-02
 * with an explicit cross-origin `Origin` header, and every byte count and
 * context window below is measured rather than assumed. Repositories that
 * require authentication are deliberately absent: meta-llama/*, google/gemma-*
 * and every Cohere Command-R repo answer 401 GatedRepo to an anonymous request,
 * so a page like this one cannot read them and listing them would be a lie.
 *
 * Where a gated model has a faithful community mirror, the mirror is used and
 * labelled as one. That choice is not cosmetic: NousResearch's Llama 3.1 mirror
 * carries a stale Llama-3.0-era 348-character template and counts the same
 * two-message conversation at 29 tokens, where the correct 4,614-character
 * Llama 3.1 template — carried identically by unsloth, mlx-community and
 * RedHatAI — counts it at 49. We use the one three independent mirrors agree on.
 */

import type { LocalEncodingSpec, ModelSpec } from '../types';

export const MODELS: ModelSpec[] = [
  {
    id: 'phi-3.5-mini',
    label: 'Phi-3.5 mini instruct',
    family: 'Phi',
    hfRepo: 'microsoft/Phi-3.5-mini-instruct',
    mirrorOf: null,
    contextWindow: 131072,
    tokenizerBytes: 1844408,
    notes: 'Smallest tokenizer in the registry — the quickest first download.',
  },
  {
    id: 'mistral-7b-v03',
    label: 'Mistral 7B Instruct v0.3',
    family: 'Mistral',
    hfRepo: 'mistralai/Mistral-7B-Instruct-v0.3',
    mirrorOf: null,
    contextWindow: 32768,
    tokenizerBytes: 1961548,
    notes: "Mistral's own ungated repo. Its template rejects a system message in some versions — chatprint shows the error rather than guessing.",
  },
  {
    id: 'smollm2-1.7b',
    label: 'SmolLM2 1.7B Instruct',
    family: 'SmolLM',
    hfRepo: 'HuggingFaceTB/SmolLM2-1.7B-Instruct',
    mirrorOf: null,
    contextWindow: 8192,
    tokenizerBytes: 2104556,
    notes: 'An 8k window — useful for seeing an overflow without pasting a novel.',
  },
  {
    id: 'granite-3.3-8b',
    label: 'Granite 3.3 8B Instruct',
    family: 'Granite',
    hfRepo: 'ibm-granite/granite-3.3-8b-instruct',
    mirrorOf: null,
    contextWindow: 131072,
    tokenizerBytes: 3476578,
    notes: "IBM's own ungated repo, with one of the more elaborate tool-calling templates.",
  },
  {
    id: 'qwen2.5-7b',
    label: 'Qwen2.5 7B Instruct',
    family: 'Qwen',
    hfRepo: 'Qwen/Qwen2.5-7B-Instruct',
    mirrorOf: null,
    contextWindow: 32768,
    tokenizerBytes: 7031645,
    notes: 'ChatML framing with a full tool-definition block — the clearest demonstration of what a tool belt costs.',
  },
  {
    id: 'deepseek-r1-qwen-7b',
    label: 'DeepSeek R1 Distill Qwen 7B',
    family: 'DeepSeek',
    hfRepo: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',
    mirrorOf: null,
    contextWindow: 131072,
    tokenizerBytes: 7031660,
  },
  {
    id: 'qwen3-8b',
    label: 'Qwen3 8B',
    family: 'Qwen',
    hfRepo: 'Qwen/Qwen3-8B',
    mirrorOf: null,
    contextWindow: 40960,
    tokenizerBytes: 11422654,
    notes: 'Its template strips previous reasoning blocks — worth watching in the segment view.',
  },
  {
    id: 'qwen3-4b-2507',
    label: 'Qwen3 4B Instruct 2507',
    family: 'Qwen',
    hfRepo: 'Qwen/Qwen3-4B-Instruct-2507',
    mirrorOf: null,
    contextWindow: 262144,
    tokenizerBytes: 11422654,
  },
  {
    id: 'phi-4-mini',
    label: 'Phi-4 mini instruct',
    family: 'Phi',
    hfRepo: 'microsoft/Phi-4-mini-instruct',
    mirrorOf: null,
    contextWindow: 131072,
    tokenizerBytes: 15524095,
  },
  {
    id: 'mistral-small-24b',
    label: 'Mistral Small 24B Instruct 2501',
    family: 'Mistral',
    hfRepo: 'mistralai/Mistral-Small-24B-Instruct-2501',
    mirrorOf: null,
    contextWindow: 32768,
    tokenizerBytes: 17078037,
  },
  {
    id: 'llama-3.1-8b',
    label: 'Llama 3.1 8B Instruct',
    family: 'Llama',
    hfRepo: 'unsloth/Meta-Llama-3.1-8B-Instruct',
    mirrorOf: 'meta-llama/Llama-3.1-8B-Instruct',
    contextWindow: 131072,
    tokenizerBytes: 17209920,
    notes: "Its template injects today's date, which is a live example of a prompt-cache breaker — see the Cache view.",
  },
  {
    id: 'llama-3.3-70b',
    label: 'Llama 3.3 70B Instruct',
    family: 'Llama',
    hfRepo: 'unsloth/Llama-3.3-70B-Instruct',
    mirrorOf: 'meta-llama/Llama-3.3-70B-Instruct',
    contextWindow: 131072,
    tokenizerBytes: 17209920,
  },
  {
    id: 'llama-3.2-3b',
    label: 'Llama 3.2 3B Instruct',
    family: 'Llama',
    hfRepo: 'unsloth/Llama-3.2-3B-Instruct',
    mirrorOf: 'meta-llama/Llama-3.2-3B-Instruct',
    contextWindow: 131072,
    tokenizerBytes: 17209920,
  },
  {
    id: 'deepseek-r1-llama-8b',
    label: 'DeepSeek R1 Distill Llama 8B',
    family: 'DeepSeek',
    hfRepo: 'unsloth/DeepSeek-R1-Distill-Llama-8B',
    mirrorOf: null,
    contextWindow: 131072,
    tokenizerBytes: 17209530,
  },
  {
    id: 'smollm3-3b',
    label: 'SmolLM3 3B',
    family: 'SmolLM',
    hfRepo: 'HuggingFaceTB/SmolLM3-3B',
    mirrorOf: null,
    contextWindow: 65536,
    tokenizerBytes: 17208819,
    notes: 'Keeps its template in a chat_template.jinja sidecar rather than tokenizer_config.json — chatprint fetches both.',
  },
  {
    id: 'gemma-2-9b',
    label: 'Gemma 2 9B IT',
    family: 'Gemma',
    hfRepo: 'unsloth/gemma-2-9b-it',
    mirrorOf: 'google/gemma-2-9b-it',
    contextWindow: 8192,
    tokenizerBytes: 17525357,
    notes: 'Gemma templates reject a system role outright. That failure is shown, not papered over.',
  },
  {
    id: 'gemma-3-4b',
    label: 'Gemma 3 4B IT',
    family: 'Gemma',
    hfRepo: 'unsloth/gemma-3-4b-it',
    mirrorOf: 'google/gemma-3-4b-it',
    contextWindow: 131072,
    tokenizerBytes: 33384568,
    notes: 'Largest tokenizer here at 31.8 MB. Its context window lives in a nested text_config, which is why other tools report it as unknown.',
  },
];

/**
 * Models whose tokenizer ships inside the bundle rather than being fetched.
 *
 * Honesty note that the UI repeats next to every figure: these encodings give
 * the exact vocabulary OpenAI uses, but the *framing* a chat request adds around
 * each message is not published for current models. chatprint applies OpenAI's
 * own documented per-message accounting, which is why these are labelled
 * "documented approximation" rather than "byte-exact" — unlike the open models
 * above, where the template is the model's own file and the count is exact.
 *
 * Anthropic publishes no local tokenizer for any model after Claude 3, so every
 * Claude figure here is an estimate and is labelled as one. Anthropic's own
 * /v1/messages/count_tokens endpoint is free and returns an exact count
 * including tools; if you need a number to bill against, use that instead.
 */
export const LOCAL_ENCODINGS: LocalEncodingSpec[] = [
  {
    id: 'gpt-4o',
    label: 'GPT-4o / GPT-4.1 / o-series',
    family: 'OpenAI',
    encoding: 'o200k_base',
    contextWindow: 128000,
    estimate: false,
    notes: 'o200k_base is the published encoding. Per-message framing follows OpenAI’s documented accounting.',
  },
  {
    id: 'gpt-4',
    label: 'GPT-4 / GPT-3.5 Turbo',
    family: 'OpenAI',
    encoding: 'cl100k_base',
    contextWindow: 128000,
    estimate: false,
    notes: 'cl100k_base is the published encoding for this generation.',
  },
  {
    id: 'claude',
    label: 'Claude (estimate only)',
    family: 'Anthropic',
    encoding: 'o200k_base',
    contextWindow: 200000,
    estimate: true,
    notes:
      'Anthropic publishes no local tokenizer after Claude 3, so this is a proxy count and can be several percent out. Their free /v1/messages/count_tokens endpoint returns the exact figure.',
  },
];

export const DEFAULT_MODEL_ID = 'qwen2.5-7b';

export function findModel(id: string): ModelSpec | undefined {
  return MODELS.find((m) => m.id === id);
}

export function findLocalEncoding(id: string): LocalEncodingSpec | undefined {
  return LOCAL_ENCODINGS.find((m) => m.id === id);
}
